import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import qs from 'querystring';

import clsx from 'clsx';

import { useDevice } from '@bonnet/next/device';

import { useFeatures } from '@atc/features';

import { Col, Row, Text } from 'reaxl';
import { clickTypes, sendClick, sendImpressions } from 'reaxl-analytics';
import { inventoryClick, inventoryImpressions } from 'reaxl-analytics-handlers';
import { InventoryListingV2 } from 'reaxl-listing';

import { saveInvSimilarListing } from '@/utilities/saveListing';

import invResultsDuck from '@/ducks/inv/invResultsDuck';
import invSimilarListingsDuck from '@/ducks/inv/invSimilarListingsDuck';
import ownersDuck from '@/ducks/ownersDuck.js';
import savedInventoryDuck from '@/ducks/savedInventoryDuck.js';

import useVdpNavigation from '@/hooks/useVdpNavigation';

function InvSimilarListingsContainer() {

    const dispatch = useDispatch();

    const navigateToVdp = useVdpNavigation();

    const device = useDevice();
    const isLessThanMd = device.lessThan.md;

    const {
        personalization: [isSaveEnabled, { listings_saved: authenticatedSavesOnly }],
        srp_listings_cropped_image: [enableFixedImageHeight],
    } = useFeatures([
        'personalization',
        'srp_listings_cropped_image',
    ]);

    const hasSentImpression = useRef(false);

    const similarListings = useSelector(invSimilarListingsDuck.selectors.getActiveInventory);
    const saveListingIds = useSelector(savedInventoryDuck.selectors.getSavedInventoryIds);

    const mainListing = useSelector(invResultsDuck.selectors.getInvActiveInventory);
    const owner = useSelector(ownersDuck.selectors.getOwners)[mainListing.ownerId];

    const similarListingIds = useMemo(() => similarListings.map((listing) => listing.id), [similarListings]);

    useEffect(() => {
        dispatch(invSimilarListingsDuck.creators.getSimilarListings({ listing: { ...mainListing, owner } }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!hasSentImpression.current && similarListingIds.length > 0) {
            sendImpressions({
                name: inventoryImpressions,
                data: {
                    selectedItemIds: similarListingIds,
                    pixallData: {
                        label: 'sim_v_lstg',
                        impressionType: 'sim',
                        listingType: 'sim',
                    },
                },
            });

            // preventing sending duplicated impression event
            hasSentImpression.current = true;
        }
    }, [similarListingIds]);

    const showSave = isSaveEnabled || authenticatedSavesOnly;
    const onSaveToggle = useCallback((event, savedListing, wasSaved) => {
        dispatch(
            saveInvSimilarListing({
                event,
                listing: savedListing,
                wasSaved,
                sendClick,
            })
        );
    }, [dispatch]);

    const handleListingAnalyticClick = useCallback((event, selectedListing) => {
        sendClick(inventoryClick, event, {
            inventoryId: selectedListing.id,
            par: 'sim_v_lstg',
            clickType: 'similar',
            pixallData: {
                eventSource: 'vehicle',
                listingType: 'sim',
            },
        });
    }, []);

    const onInventoryListingClick = useCallback((event, selectedListing) => {
        // need to stop propagation and prevent default to allow anchors within listing for SEO purposes while using the
        // correct handlers
        event.stopPropagation();
        event.preventDefault();

        const targetClassName = event && event.target && event.target.className || [];
        const isSaveClick = targetClassName.includes('glyphicon-heart-o');
        const isSelectClick = targetClassName.includes('input-label') && event.target.innerHTML === 'Select';

        if (!(isSaveClick || isSelectClick) && selectedListing) {
            handleListingAnalyticClick(event, selectedListing);

            if (selectedListing.id) {
                const urlParams = qs.parse(window.location.search.substring(1));
                delete urlParams.listingId;
                delete urlParams.clickType;

                dispatch(invSimilarListingsDuck.creators.clear());

                navigateToVdp({
                    ...urlParams,
                    listingId: selectedListing.id,
                    clickType: clickTypes.SIMILAR_HYBRID,
                    generateReferrer: false,
                });
            } else {
                return false;
            }
        }

        return true;
    }, [dispatch, navigateToVdp, handleListingAnalyticClick]);

    const renderListings = similarListings.map((listing, index) => {
        const onClick = (e) => onInventoryListingClick(e, listing, index);

        return (
            <Col
                key={`listing-${listing.id}-${index}`}
                xs={12}
                sm={4}
                md={3}
                className="display-flex"
            >
                <InventoryListingV2
                    className={clsx({ 'image-fixed-height': enableFixedImageHeight }, 'cursor-pointer')}
                    isSaved={saveListingIds.includes(listing.id)}
                    listing={listing}
                    loading="eager"
                    onClick={onClick}
                    onSaveToggle={showSave && onSaveToggle}
                    orientation="portrait"
                    showFooter={false}
                />
            </Col>
        );
    });

    return (
        <>
            <Text
                className="margin-vertical-4"
                componentClass="div"
                color="primary"
                size={isLessThanMd ? 500 : 600}
                weight="ultra-bold"
            >
                Similar Cars to Consider
            </Text>
            <Row className="display-flex flex-wrap">
                {renderListings}
            </Row>
        </>
    );
}

export default InvSimilarListingsContainer;
